import { DashboardStat, DealData, Entry, StatsData } from "../types";
import { Activity, ArrowDownToLine, Coins, Gift, Languages, ScanFace, ShoppingCart, Smartphone, Tablet, TrendingUp, UserCheck, UserCog, UserLock, UserRoundMinus, Users } from "lucide-react";
import { getDailyUsersStatementReport, getDemoUsersStatementsReport, getEsouqOrdersReport, getInvestmentInsightsReport, getMoneyTransactionsReport, getStatementsReport, getUsersCashbackReport } from "../../store/admin/reports-slice";
import { queryParams } from "../interface";
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import Decimal from "decimal.js";
import { getLiveTransactions } from "../../store/admin/liveTransactions-slice";
import { ADMIN_STORAGE_KEYS } from "../../config/storageKeys";

export const getStoredUser = () => {
  const stored = localStorage.getItem(ADMIN_STORAGE_KEYS.userInfo);
  return stored ? JSON.parse(stored) : undefined;
};

export const isImageFile = (file: File): boolean => {
  return file.type.includes('jpg') || file.type.includes('jpeg') || file.type.includes('png');
};

export function isEmpty(str?: string | null): boolean {
  return !str || str.trim().length === 0;
}

const statDefinitions: Omit<DashboardStat, "value">[] = [
  { metric: "totalOrders", title: "Total Orders", color: "#FF6384", icon: ShoppingCart, path: "/e-souq-manager/orders" },
  { metric: "totalUsers", title: "Total Users", color: "#36A2EB", icon: Users, path: "/clients" },
  { metric: "totalUserInfoRequests", title: "User Info Change Requests", color: "#FFCE56", icon: UserCog, path: "/requests/user-change-requests" },
  { metric: "totalGifts", title: "Total Gifts", color: "#8884D8", icon: Gift },
  { metric: "totalWithdrawRequests", title: "Withdrawal Requests", color: "#4BC0C0", icon: ArrowDownToLine, path: "/requests/pending-withdrawal-requests" },
  { metric: "totalDeposits", title: "Total Deposits", color: "#9CA3AF", icon: Coins, path: "/reports/money-transactions" },
  { metric: "totalMoneytransactions", title: "Total Money Transactions", color: "#FF8042", icon: Activity, path: "/reports/money-transactions" },
  { metric: "totalTrades", title: "Total Trades", color: "#FFBB28", icon: TrendingUp, path: "/history" },
  { metric: "totalRealUsers", title: "Total Real Users", color: "#24bd1c", icon: UserCheck, path: "/clients" },
  { metric: "totalDemoUsers", title: "Total Demo Users", color: "#e3d219", icon: UserRoundMinus, path: "/reports/demo-users-statements" },
  { metric: "totaliOSMobileUsers", title: "Total iOS Mobile Users", color: "#FF8042", icon: Smartphone },
  { metric: "totalAndroidMobileUsers", title: "Total Android Mobile Users", color: "#FFBB28", icon: Smartphone },
  { metric: "totaliOSTabletUsers", title: "Total iOS Tablet Users", color: "#a53eb3", icon: Tablet },
  { metric: "totalAndroidTabletUsers", title: "Total Android Tablet Users", color: "#cc3f4d", icon: Tablet },
  { metric: "totalKYCUsers", title: "Total KYC Users", color: "#24bd1c", icon: ScanFace, path: "/compliance/kyc" },
  { metric: "totalLockedUsers", title: "Total Locked Users", color: "#e3d219", icon: UserLock },
  { metric: "totalEngUsers", title: "Total English Lang Users", color: "#FF8042", icon: Languages },
  { metric: "totalArabicUsers", title: "Total Arabic Lang Users", color: "#FFBB28", icon: Languages },
];

export const convertToComponentFormat = (data: StatsData): DashboardStat[] => {
  return statDefinitions.map((stat) => ({ ...stat, value: data[stat.metric] }));
};

export const getPricePerOunce = (data: Entry[]) => ({
  sellingOunce: Number(data.filter(item => item.t === "Bid").at(-1)?.b),
  buyingOunce: Number(data.filter(item => item.t === "Offer").at(-1)?.a),
});

export const toWhatsAppNumber = (phone?: string) =>
  (phone || "").replace(/\D/g, "").replace(/^0+/, "");

export const formatPrice = (price: number | string): number => {
  let num: number;

  if (typeof price === 'string') {
    num = parseFloat(price.replace(/,/g, ''));
    if (isNaN(num)) {
      throw new Error(`Invalid price string: ${price}`);
    }
  } else {
    num = price;
  }

  if (isNaN(num) || !isFinite(num)) {
    throw new Error(`Invalid price value: ${price}`);
  }

  return Number(num.toFixed(2));
};

export const initialSearchTerm = () => {
  const searchParams = new URLSearchParams(location.search);
  return searchParams.get("search") || "";
};

export const uncapitalizeWord = (str: string): string => {
  return str.charAt(0).toLowerCase() + str.slice(1);
}

export const getReportPageUrl = (type: string, clientId: string, reportType?: string) => {
  return `${window.location.origin}/${type}${reportType ? reportType : ""}/?search=${clientId}`
}

export const isEmptyObject = (obj: any) => {
  return Object.keys(obj).length === 0;
}

export const preprocessAccount = (account: string): string => {
  if (!account) return "";
  const [id, name] = account.split("-");
  const shortName = name?.split(" ").slice(0, 2).join(" ");

  return `${id}-${shortName}`;
}

export const getReportAPIFunc = (reportType: string, paramPage: number, paramLimit: number, search: string, startDate: string, endDate: string, month?: number, year?: number) => {
  switch (reportType) {
    case "live_transactions":
      return getLiveTransactions({ paramPage, paramLimit, search, startDate, endDate })
    case "money_transactions":
      return getMoneyTransactionsReport({ paramPage, paramLimit, search, startDate, endDate });
    // case "Loans-Report":
    //   return getLoansReport({ search, startDate, endDate });
    case "esouq_orders":
      return getEsouqOrdersReport({ paramPage, paramLimit, search, startDate, endDate });
    case "statements":
      return getStatementsReport({ paramPage, paramLimit, search, startDate, endDate });
    case "demo_users_statements":
      return getDemoUsersStatementsReport({ paramPage, paramLimit, search, startDate, endDate });
    case "daily_users_statement":
      return getDailyUsersStatementReport({ paramPage, paramLimit, search, startDate, endDate });
    case "users_cashback":
      return getUsersCashbackReport({ paramPage, paramLimit, search, startDate, endDate, month, year });
    case "investment_insights":
      return getInvestmentInsightsReport({ paramPage, paramLimit, search, startDate, endDate });
    default:
      return null;
  }
}

export const buildUrlWithParams = (endpoint: string, { paramPage, paramLimit, search, startDate, endDate, month, year, equityOperator, equityValue, userType, transactionType, filters }: queryParams): string => {
  const params = new URLSearchParams();
  if (paramPage) params.set('paramPage', paramPage.toString());
  if (paramLimit) params.set('paramLimit', paramLimit.toString())
  if (search) params.set('search', search);
  if (startDate) params.set('startDate', startDate.toString());
  if (endDate) params.set('endDate', endDate.toString());
  if (month) params.set('month', month.toString());
  if (year) params.set('year', year.toString());
  if (equityOperator) params.set('equityOperator', equityOperator.toString());
  if (equityValue) params.set('equityValue', equityValue.toString());
  if (userType) params.set('userType', userType);
  if (transactionType) params.set('transactionType', transactionType);
  Object.entries(filters || {}).forEach(([key, value]) => {
    if (value && value !== 'All') params.set(key, value);
  });
  return `/${endpoint}${params.toString() ? `?${params.toString()}` : ''}`;
};

export const getStatusColor = (status: string) => {
  switch (status) {
    case 'approved':
    case 'completed':
      return 'text-green-300 bg-green-900/20';
    case 'rejected':
    case 'failed':
      return 'text-red-300 bg-red-900/20';
    default:
      return 'text-yellow-300 bg-yellow-900/20';
  }
}

export const isPhoneValid = (phone: string): boolean => {
  try {
    if (!phone || phone.length < 4) return false;

    // Ensure phone starts with +
    const phoneWithPlus = phone.startsWith('+') ? phone : '+' + phone;

    const phoneNumber = parsePhoneNumberFromString(phoneWithPlus);
    return !!phoneNumber && phoneNumber.isValid();
  } catch (error) {
    console.error('Phone validation error:', error);
    return false;
  }
};

export const calculateEsouqMetalProductPrice = (deals: DealData[]) => {
  const total = deals.reduce((acc, item) => {
    const price = new Decimal(item.tradeId.buyingPrice ?? item.tradeId.buyAtPrice).toDecimalPlaces(2);
    const metal = new Decimal(item.amount).toDecimalPlaces(2);
    return new Decimal(acc).plus(price.times(metal));
  }, new Decimal(0));
  return total.toDecimalPlaces(2, Decimal.ROUND_DOWN).toNumber();
};

export const getInitials = (name: string) => {
  return name
    .split(" ")
    .slice(0, 2)
    .map((n) => n[0])
    .join("")
    .toUpperCase();
}

export const getDefaultDateRange = () => {
  const now = new Date();
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  return { startDate: startOfMonth, endDate: now };
};

// Amounts in AED or grams: thousands separators and at most 2 decimals ("9,382.18", "4,722")
export const formatAmount = (value: number): string =>
  value.toLocaleString(undefined, { maximumFractionDigits: 2 });

export const formatFileSize = (bytes: number): string =>
  bytes < 1024 * 1024 ? `${Math.max(1, Math.round(bytes / 1024))} KB` : `${(bytes / (1024 * 1024)).toFixed(1)} MB`;